import { useEffect, useState } from "react";

import Eyebrow from "../../Eyebrow";
import Reveal from "../../Reveal";
import SpotlightCard from "../../SpotlightCard";
import TechNote from "../TechNote";
import { howItWorksSections } from "../../../../content/howItWorksSections";

const MOBILE_BREAKPOINT = 900;

const BORDER_STROKE = "rgba(255,255,255,0.4)";
const BORDER_WIDTH = 1.2;

/**
 * Derives every coordinate the diagram needs (grid nodes, panel edges, label
 * positions) from a small set of design values, so the desktop and mobile
 * variants share one set of arithmetic instead of two hand-duplicated ones.
 * Translation: Derives every coordinate the diagram needs (grid nodes, panel edges, label
 * positions) from a small set of design values, so the desktop and mobile
 * variants share one set of arithmetic instead of two hand-duplicated ones.
 *
 * @param {Object} opts - Layout design values.
 * @returns {Object} Fully resolved layout (nodes, panel box, tiers, viewBox).
 */
const createLayout = ({
  cols,
  rows,
  nodeR,
  nodeFont,
  gridLeft,
  gridRight,
  gridTop,
  gridBottom,
  panelPad,
  labelBand,
  sourceY,
  sourceR,
  mainX,
  mainY,
  mainR,
  outputY,
  outputR,
  tierX,
  viewWidth,
  viewHeight,
}) => {
  const colStep = cols > 1 ? (gridRight - gridLeft) / (cols - 1) : 0;
  const rowStep = (gridBottom - gridTop) / (rows - 1);

  const nodes = Array.from({ length: cols * rows }, (_, i) => {
    const row = Math.floor(i / cols);
    const col = i % cols;
    return {
      id: `${row}-${col}`,
      x: cols > 1 ? gridLeft + col * colStep : (gridLeft + gridRight) / 2,
      y: gridTop + row * rowStep,
      label: `${String.fromCharCode(65 + row)}${col + 1}`,
    };
  });

  const panelTop = gridTop - panelPad - labelBand;

  return {
    nodes,
    nodeR,
    nodeFont,
    panelLeft: gridLeft - panelPad,
    panelRight: gridRight + panelPad,
    panelTop,
    panelBottom: gridBottom + panelPad,
    labelY: panelTop + 26,
    captionY: panelTop + 46,
    sourceY,
    sourceR,
    mainX,
    mainY,
    mainR,
    outputY,
    outputR,
    tierX,
    viewWidth,
    viewHeight,
  };
};

const DESKTOP_LAYOUT = createLayout({
  cols: 9,
  rows: 9,
  nodeR: 24,
  nodeFont: 12,
  gridLeft: 178,
  gridRight: 722,
  gridTop: 260,
  gridBottom: 804,
  panelPad: 60,
  labelBand: 50,
  sourceY: 60,
  sourceR: 50,
  mainX: 450,
  mainY: 964,
  mainR: 70,
  outputY: 1170,
  outputR: 46,
  tierX: [225, 450, 675],
  viewWidth: 900,
  viewHeight: 1260,
});

// Narrower grid (3 columns × 9 rows instead of 9×9) with larger source/output/
// main circles so everything stays legible on a phone-width viewport.
const MOBILE_LAYOUT = createLayout({
  cols: 3,
  rows: 9,
  nodeR: 16,
  nodeFont: 12,
  gridLeft: 85,
  gridRight: 255,
  gridTop: 190,
  gridBottom: 550,
  panelPad: 40,
  labelBand: 40,
  sourceY: 46,
  sourceR: 40,
  mainX: 170,
  mainY: 702,
  mainR: 52,
  outputY: 854,
  outputR: 40,
  tierX: [70, 170, 270],
  viewWidth: 340,
  viewHeight: 940,
});

/**
 * Tracks whether the viewport is at or below the page's mobile breakpoint.
 * Translation: Tracks whether the viewport is at or below the page's mobile breakpoint.
 *
 * @returns {boolean} True when the viewport is mobile-width.
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

/**
 * Two centered lines of text inside a circular node.
 * Translation: Two centered lines of text inside a circular node.
 *
 * @param {Object} props - Node label props.
 * @param {number} props.x - Circle center x.
 * @param {number} props.y - Circle center y.
 * @param {string[]} props.lines - One or two lines of label text.
 * @param {string} props.color - Text fill color.
 * @param {number} [props.fontSize=13] - Font size in px.
 * @returns {JSX.Element} Stacked text lines.
 */
const NodeLabel = ({ x, y, lines, color, fontSize = 13 }) => {
  const lineHeight = fontSize + 4;
  const startY = lines.length > 1 ? y - lineHeight / 2 + fontSize / 2.5 : y + fontSize / 3;

  return (
    <>
      {lines.map((line, index) => (
        <text
          key={line}
          x={x}
          y={startY + index * lineHeight}
          textAnchor="middle"
          fontSize={fontSize}
          fontFamily="Public Sans, sans-serif"
          fontWeight="300"
          fill={color}
        >
          {line}
        </text>
      ))}
    </>
  );
};

/**
 * A single animated dashed connector between two points.
 * Translation: A single animated dashed connector between two points.
 *
 * @param {Object} props - Connector props.
 * @param {number} props.x1 - Start x.
 * @param {number} props.y1 - Start y.
 * @param {number} props.x2 - End x.
 * @param {number} props.y2 - End y.
 * @param {number} [props.delay=0] - Animation begin offset, in seconds.
 * @returns {JSX.Element} Dashed connector line.
 */
const Connector = ({ x1, y1, x2, y2, delay = 0 }) => (
  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH} strokeDasharray="3 5">
    <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" begin={`${delay}s`} repeatCount="indefinite" />
  </line>
);

/**
 * Full "agentic MapReduce swarm" diagram: source data feeding a grid of
 * parallel read-only agents, reduced by one orchestrator, fanning out to
 * verified output. Layout switches between a 9x9 desktop grid and a
 * narrower 3x9 mobile grid with larger source/output circles.
 * Translation: Full "agentic MapReduce swarm" diagram: source data feeding a grid of
 * parallel read-only agents, reduced by one orchestrator, fanning out to
 * verified output. Layout switches between a 9x9 desktop grid and a
 * narrower 3x9 mobile grid with larger source/output circles.
 *
 * @param {Object} props - Engine viz props.
 * @param {Object} props.engine - Engine content block (source/output/main node copy).
 * @returns {JSX.Element} Engine diagram.
 */
const EngineViz = ({ engine }) => {
  const isMobile = useIsMobile();
  const layout = isMobile ? MOBILE_LAYOUT : DESKTOP_LAYOUT;
  const {
    nodes, nodeR, nodeFont, panelLeft, panelRight, panelTop, panelBottom, labelY, captionY,
    sourceY, sourceR, mainX, mainY, mainR, outputY, outputR, tierX, viewWidth, viewHeight,
  } = layout;

  return (
    <svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      {tierX.map((x, i) => (
        <Connector key={`src-${i}`} x1={x} y1={sourceY + sourceR} x2={x} y2={panelTop} delay={i * 0.3} />
      ))}

      <rect
        x={panelLeft}
        y={panelTop}
        width={panelRight - panelLeft}
        height={panelBottom - panelTop}
        rx="24"
        fill="rgba(255,255,255,0.04)"
        stroke={BORDER_STROKE}
        strokeWidth={BORDER_WIDTH}
      >
        <animate attributeName="opacity" values="0.6;1;0.6" dur="4s" repeatCount="indefinite" />
      </rect>

      <text x={mainX} y={labelY} textAnchor="middle" fontSize="14" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.08em" fill="rgba(255,255,255,0.85)">
        {engine.gridLabel.label}
      </text>
      <text x={mainX} y={captionY} textAnchor="middle" fontSize="12" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.06em" fill="rgba(255,255,255,0.55)">
        {engine.gridLabel.caption}
      </text>

      {nodes.map((node) => (
        <g key={node.id}>
          <circle cx={node.x} cy={node.y} r={nodeR} fill="rgba(255,255,255,0.06)" stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH} />
          <text
            x={node.x}
            y={node.y + nodeFont / 3}
            textAnchor="middle"
            fontSize={nodeFont}
            fontFamily="Public Sans, sans-serif"
            fontWeight="300"
            fill="rgba(255,255,255,0.7)"
          >
            {node.label}
          </text>
        </g>
      ))}

      {tierX.map((x, i) => (
        <Connector key={`reduce-${i}`} x1={x} y1={panelBottom} x2={mainX} y2={mainY - mainR} delay={i * 0.3} />
      ))}

      <circle cx={mainX} cy={mainY} r={mainR} fill="none" stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH}>
        <animate attributeName="r" values={`${mainR};${mainR + 18};${mainR}`} dur="4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.6;0;0.6" dur="4s" repeatCount="indefinite" />
      </circle>
      <circle cx={mainX} cy={mainY} r={mainR} fill="rgba(255,255,255,0.07)" stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH} />
      {isMobile ? (
        <>
          {engine.mainNode.label.split(" ").map((word, index) => (
            <text
              key={word}
              x={mainX}
              y={mainY - 18 + index * 18}
              textAnchor="middle"
              fontSize="16"
              fontFamily="Public Sans, sans-serif"
              fontWeight="300"
              letterSpacing="0.06em"
              fill="rgba(255,255,255,0.94)"
            >
              {word}
            </text>
          ))}
          <text x={mainX} y={mainY + 20} textAnchor="middle" fontSize="10" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.08em" fill="rgba(255,255,255,0.6)">
            {engine.mainNode.caption}
          </text>
        </>
      ) : (
        <>
          <text x={mainX} y={mainY - 4} textAnchor="middle" fontSize="17" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.06em" fill="rgba(255,255,255,0.94)">
            {engine.mainNode.label}
          </text>
          <text x={mainX} y={mainY + 18} textAnchor="middle" fontSize="12" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.08em" fill="rgba(255,255,255,0.6)">
            {engine.mainNode.caption}
          </text>
        </>
      )}

      {tierX.map((x, i) => (
        <Connector key={`out-${i}`} x1={mainX} y1={mainY + mainR} x2={x} y2={outputY - outputR} delay={i * 0.3} />
      ))}

      {engine.sourceNodes.map((node, i) => (
        <g key={`source-${i}`}>
          <circle cx={tierX[i]} cy={sourceY} r={sourceR} fill="rgba(255,255,255,0.05)" stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH} />
          <NodeLabel
            x={tierX[i]}
            y={sourceY}
            lines={isMobile && node.mobileLines ? node.mobileLines : node.lines}
            color="rgba(255,255,255,0.8)"
          />
        </g>
      ))}

      {engine.outputNodes.map((node, i) => (
        <g key={`output-${i}`}>
          <circle cx={tierX[i]} cy={outputY} r={outputR} fill="rgba(255,255,255,0.06)" stroke={BORDER_STROKE} strokeWidth={BORDER_WIDTH}>
            <animate attributeName="opacity" values="1;0.6;1" dur="2.4s" begin={`${i * 0.4}s`} repeatCount="indefinite" />
          </circle>
          <NodeLabel x={tierX[i]} y={outputY} lines={node.lines} color="rgba(255,255,255,0.9)" />
        </g>
      ))}
    </svg>
  );
};

/**
 * Flagship "engine" section: title, description, and an animated diagram of
 * the agentic MapReduce swarm underlying every capability on the page.
 * Translation: Flagship "engine" section: title, description, and an animated diagram of
 * the agentic MapReduce swarm underlying every capability on the page.
 *
 * @returns {JSX.Element} Engine section.
 */
const Engine = () => {
  const { engine, techNotes } = howItWorksSections;

  return (
    <section id="engine" className="section-block">
      <div className="section-shell">
        <div className="hiw-engine-split">
          <div className="hiw-engine-col">
            <Reveal>
              <Eyebrow>
                {engine.eyebrowLabel}
                <TechNote>{techNotes[engine.techNote]}</TechNote>
              </Eyebrow>
              <h2 className="section-heading__title">{engine.title}</h2>
              <p className="section-heading__intro">{engine.description}</p>
            </Reveal>

            <div className="hiw-engine-legend">
              {engine.legend.map((item, index) => (
                <Reveal key={item.title} delay={index * 80}>
                  <SpotlightCard className="glass-panel hiw-engine-legend__item">
                    <strong>{item.title}</strong>
                    <p>{item.description}</p>
                  </SpotlightCard>
                </Reveal>
              ))}
            </div>

            {engine.detail && (
              <Reveal>
                <p className="hiw-detail-text">{engine.detail}</p>
              </Reveal>
            )}
          </div>

          <Reveal>
            <div className="hiw-engine-viz" aria-hidden="true">
              <EngineViz engine={engine} />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Engine;
